Observable from @danielx/observable

type { MidiEvent } from @danielx/lib

{ addMidiEventListener } from ./lib/midi/input.civet
{ buildNoteIndex, indexNoteIds, type IndexedNote } from ./lib/midi/note-index.civet
{ GameScore } from ./lib/midi/game-score.civet
{ PracticeSession, HIT_WINDOW, type Judgment } from ./lib/midi/practice-session.civet
{ readPracticeMetadata, writePracticeMetadata, practiceChannels, type PracticeMetadata } from ./lib/midi/practice-metadata.civet
{ autoPracticeMetadata } from ./lib/midi/auto-practice.civet
{ parseSynthesiaXML, synthesiaToPracticeMetadata, normalizeMd5Id, type SynthesiaSong } from ./lib/midi/synthesia.civet
{ md5Hex } from ./lib/md5.civet
{ JUDGMENT_COLORS, JUDGMENT_FEEDBACK_MS, ROLL_BG_COLOR } from ./const.civet

export type PracticeFeedback =
  n: number
  judgment: Judgment
  // The song beat the judgment anchors to: the chart note's beat, or the
  // playhead for a wrong note. The staff view draws it there.
  beat: number
  // performance.now() when it happened — how the overlays fade it out
  at: number
  // Combo multiplier tier at the moment of the hit (1 outside game mode);
  // the views scale their burst particles with it
  tier: number

/**
Practice mode: the player performs their part of the song on a MIDI keyboard
while the rest plays back. Which channels make up that part comes from the
song's practice metadata (see practice-metadata.civet) — entering practice on
a song without metadata offers to create it, and a running practice assumes
the metadata is complete. The part's scheduled events are muted (see
suppressScheduledEvent, consulted by PlayerAudio), live input is rerouted onto
it so it sounds with its own instrument, and each played note is judged
against the chart (see PracticeSession).
*/
export PracticeMode := <Base extends Constructor<{}>>(S: Base) ->
  class PracticeMode extends S
    // "edit" is the ordinary editor; "practice" mutes the practiced part and
    // judges MIDI input against it; "game" is the same performance path with
    // scoring on and the practice aids (rate, loop, wait) and editing off
    playMode = Observable "edit"
    // Recent judgments for the views to draw; pruned as they fade
    practiceFeedback: PracticeFeedback[] = []
    // Slow-down, 0.5–1.0. Applied at the transport clock (see playbackRate in
    // player-audio.civet), never the tempo map; judgment windows stay in real
    // seconds (see PracticeSession's rate).
    practiceRate = Observable 1
    // A/B loop as [startBeat, endBeat], or undefined for none. The transport
    // wraps within it while practicing (practiceLoopRange).
    practiceLoop = Observable undefined as [number, number]?
    // A pressed, B not yet: the pending loop start beat
    practiceLoopStart = Observable undefined as number?
    // Wait-for-note: the clock freezes at each chart note (a chord is one
    // gate) until the player plays it — see practiceGateTime and the freeze
    // in player-audio.civet's bufferAudio
    waitForNote = Observable false
    // Game Mode display state, fed from the pass's GameScore for the HUD
    gameHudScore = Observable 0
    gameHudCombo = Observable 0
    gameHudMultiplier = Observable 1
    gameHudHealth = Observable 1
    // Which mode the practice setup dialog should start once metadata exists
    practiceSetupTarget: "practice" | "game" = "practice"
    // Synthetic frame clock for offline renders (renderVideo drives it): the
    // views take it in place of performance.now(), so feedback fades, bursts,
    // and HUD pops track video time rather than wall time. Unset live.
    fxNow?: () => number
    // Beats to shift the roll's beat/measure grid later, for songs with a
    // pickup (renderVideo's beatOffset option drives it). Unset live.
    gridBeatOffset?: () => number

    initPractice: () => void
    togglePractice: () => void
    enterMode: (mode: "edit" | "practice" | "game") => void
    toggleWaitForNote: () => void
    // ✏️/🎯/🎮 for the actions bar's mode button
    modeIcon: () => string
    startPractice: (mode?: "practice" | "game") => void
    // Assigned by the practice setup view (views/practice-setup.civet)
    showPracticeSetup?: () => void
    showSongCollection?: (target?: "practice" | "game") => void
    // Assigned by the mode switcher modal (views/mode-modal.civet)
    showModeModal?: () => void
    // Assigned by the lesson view (views/my-practice.civet)
    showMyPractice?: () => void
    lessonNoteOn?: (n: number) => void
    // Assigned by the results view (views/game-results.civet)
    showGameResults?: (results: {stats: PracticeSession["stats"], score: number, maxCombo: number, failed?: boolean}) => void
    playbackRate: () => number
    practiceLoopRange: () => [number, number]?
    practiceGateTime: () => number?
    suppressSongLoop: () => boolean
    cyclePracticeLoop: () => void
    suppressScheduledEvent: (e: MidiEvent) => boolean
    // True for a channel the views should leave undrawn: while performing,
    // everything outside the practiced part
    practiceHidesChannel: (c: number) => boolean
    practiceRouteMidiData: (data: Uint8Array) => Uint8Array?
    practiceStats: () => PracticeSession["stats"]?
    // note id -> fingering from the song's metadata, for the views
    practiceFingering: () => Record<string, string>?
    // Judgment palette and roll backdrop with the song theme's overrides
    // applied — the views read these per frame (cached per theme object)
    practiceJudgmentColors: () => Record<string, number>
    rollBackground: () => number
    // .synthesia interchange, called from Persistence's fromBlob
    importSynthesiaMetadata: (xml: string) => void
    practiceMidiImported: (bytes: Uint8Array) => void

    constructor(...args: any[])
      super ...args

      let session: PracticeSession?
      let gameScore: GameScore?
      let noteIndex: IndexedNote[]?
      // The channels of the practiced part(s), from the song's metadata;
      // rebuilt whenever practice starts or the song changes
      practiceSet .= new Set<number>
      primaryChannel .= 0
      lastTime .= 0

      // When any part is note-level (a Synthesia hand split), only those
      // exact notes are judged; whole-channel parts judge the whole channel
      let practiceNoteIds: Set<string>?
      let practiceChannelParts: Set<number>

      refreshPracticeSet := =>
        meta := readPracticeMetadata @song
        practiceSet = new Set if meta then practiceChannels(meta) else []
        primaryChannel = practiceSet.values().next().value ?? 0

        practiceChannelParts = new Set
        noteIds := new Set<string>
        meta?.parts.forEach (part) =>
          part.channels?.forEach (c) => practiceChannelParts.add c
          part.notes?.forEach (id) => noteIds.add id
        practiceNoteIds = if noteIds.size then noteIds else undefined

      chartNotes := =>
        noteIndex ?= buildNoteIndex @song
        return noteIndex.filter (note) -> practiceSet.has note.c unless practiceNoteIds

        ids := indexNoteIds noteIndex, @song.ticksPerBeat
        noteIndex.filter (note, i) ->
          practiceChannelParts.has(note.c) or practiceNoteIds!.has ids[i]

      // The performance modes share everything below; "game" adds scoring
      performing := =>
        m := @playMode()
        m is "practice" or m is "game"

      updateHud := =>
        @gameHudScore gameScore?.score ?? 0
        @gameHudCombo gameScore?.combo ?? 0
        @gameHudMultiplier gameScore?.multiplier() ?? 1
        @gameHudHealth gameScore?.health ?? 1

      // The rate, mode, and loop the running session was created at —
      // finishPass reports against these, since the observables may have
      // just changed (that change is often what triggered the reset)
      sessionRate .= 1
      sessionMode .= "edit"
      let sessionLoop: [number, number]?

      // Flush the running pass before the session is replaced or dropped:
      // triggers "practicePassEnded" on the player's event bus with
      // {stats, rate, loop, complete} — `complete` meaning the pass reached
      // the loop's end; consumers (the lesson system's section progress)
      // ignore abandoned ones. lastTime still holds the clock's position at
      // the pass's end (the reset is what moved it). Everything is judged
      // against the session's own snapshot: a game session never claims
      // practice stats no matter which mode we exit into, and a drill switch
      // that swapped the loop still attributes the pass to the loop it ran.
      finishPass := =>
        return unless session and sessionMode is "practice"
        range := sessionLoop
        return unless range
        complete := lastTime >= @song.timeAtBeat(range[1]) - 0.35
        // The wrap resets the clock before it reaches the loop's end, so
        // unresolved notes in the final hit-window would escape as neither
        // hit nor missed — a clean pass with its last note unplayed
        session.finishThrough @song.timeAtBeat(range[1]) if complete
        @trigger "practicePassEnded", {
          stats: session.stats
          rate: sessionRate
          loop: range
          complete
        }

      resetSession := =>
        finishPass()
        unless performing()
          session = undefined
          return
        lastTime = @currentTime()
        // playbackRate, not practiceRate: game locks the rate to 1
        sessionRate = @playbackRate()
        sessionMode = @playMode()
        sessionLoop = @practiceLoop()
        session = new PracticeSession chartNotes(), lastTime, @playbackRate()
        gameScore = if @playMode() is "game" then new GameScore else undefined
        gameOverShown = false
        updateHud()

      // shown once per pass; reset with the session
      gameOverShown .= false

      recordJudgment := (judgment: Judgment) =>
        return unless gameScore
        gameScore.judge judgment
        updateHud()
        // Health emptied: the pass fails on the spot. Stop the transport and
        // show the results as a fail (the natural-finish path won't fire —
        // it requires the playhead at the end).
        if gameScore.failed() and not gameOverShown and session
          gameOverShown = true
          @pause() if @playing()
          @showGameResults?.({
            stats: session.stats
            score: gameScore.score
            maxCombo: gameScore.maxCombo
            failed: true
          })

      pushFeedback := (n: number, judgment: Judgment, beat: number) =>
        list := @practiceFeedback
        now := performance.now()
        while list.length and now - list[0].at > JUDGMENT_FEEDBACK_MS
          list.shift()
        list.push {n, judgment, beat, at: now, tier: gameScore?.multiplier() ?? 1}

      @practiceHidesChannel = (c) =>
        performing() and not practiceSet.has c

      @suppressScheduledEvent = (e) =>
        return false unless performing()
        {type} := e
        return false unless type is "noteOn" or type is "noteOff"
        practiceSet.has e.c

      @practiceRouteMidiData = (data) =>
        return unless performing()
        // channel voice messages only; system messages pass through untouched
        return unless 0x80 <= data[0] < 0xf0
        rerouted := new Uint8Array data
        rerouted[0] = (data[0] & 0xf0) + primaryChannel
        rerouted

      @startPractice = (mode: "practice" | "game" = "practice") =>
        refreshPracticeSet()
        @playMode mode

      // Direct entry, used by the mode modal: no toggling, "edit" always allowed
      @enterMode = (mode) =>
        if mode is "edit"
          @playMode "edit"
          return
        unless @purchased()
          @purchase()
          return
        return if @playMode() is mode
        meta := readPracticeMetadata @song
        if meta and practiceChannels(meta).length
          @startPractice mode
        else if auto := autoPracticeMetadata @song
          // No setup yet but there are notes: configure automatically —
          // sections and the melody part from the structural analysis — and
          // start. The setup editor refines this rather than gating entry.
          writePracticeMetadata @song, auto
          @pushState @getState()
          @startPractice mode
        else
          // An empty song has nothing to practice: offer something to play
          @showSongCollection?(mode)

      @togglePractice = =>
        if @playMode() is "practice"
          @playMode "edit"
        else
          @enterMode "practice"

      @toggleWaitForNote = =>
        @waitForNote !@waitForNote()

      @modeIcon = =>
        switch @playMode()
          "practice" then "🎯"
          "game" then "🎮"
          else "✏️"

      // Consulted by the transport (player-audio.civet) every buffer tick
      @playbackRate = =>
        if @playMode() is "practice" then @practiceRate() else 1

      @practiceLoopRange = =>
        @practiceLoop() if @playMode() is "practice"

      // The gate blocking the transport: the next chart moment with a note
      // the player has neither hit nor (before wait mode came on) missed
      @practiceGateTime = =>
        return unless @playMode() is "practice" and @waitForNote()
        session?.nextGateTime()

      // A game pass is one play-through: the whole-song loop would otherwise
      // reset the session forever and the results would never show
      @suppressSongLoop = =>
        @playMode() is "game"

      // One button walks the loop through its states: no loop → A set at the
      // playhead → B set at the playhead (loop live) → cleared.
      @cyclePracticeLoop = =>
        a := @practiceLoopStart()
        if @practiceLoop()
          @practiceLoop undefined
        else if a?
          b := @currentBeat()
          if b > a
            @practiceLoopStart undefined
            @practiceLoop [a, b]
          else
            // B at or before A: treat as re-placing A
            @practiceLoopStart b
        else
          @practiceLoopStart @currentBeat()

      @practiceStats = =>
        session?.stats

      // Judgment colors and the roll backdrop come from the song's theme when
      // it overrides them (theme-editor); the burst particles inherit the
      // judgment palette for free. Cached per theme object — the views call
      // these every frame.
      let themedCache: {src: unknown, judgment: Record<string, number>, rollBg: number}?
      themed := =>
        t := @song.theme() as unknown as {judgmentColors?: Record<string, number>, rollBackground?: number}?
        unless themedCache and themedCache.src is t
          themedCache = {
            src: t
            judgment: {...JUDGMENT_COLORS, ...t?.judgmentColors}
            rollBg: t?.rollBackground ?? ROLL_BG_COLOR
          }
        themedCache

      @practiceJudgmentColors = => themed().judgment
      @rollBackground = => themed().rollBg

      // Fingering is score annotation, not practice state: it draws in every
      // mode whenever the song's metadata has it
      fingeringDirty .= true
      let fingeringCache: Record<string, string>?
      @practiceFingering = =>
        if fingeringDirty
          fingeringDirty = false
          fingeringCache = readPracticeMetadata(@song)?.fingering
        fingeringCache

      // .synthesia sidecar files are keyed by the MD5 of the music file, so
      // matching works in either import order: the registry holds every
      // entry seen this session, and the original bytes of the last MIDI
      // import stick around to convert against (conversion is track-scoped
      // and our song model flattens tracks — see synthesia.civet).
      synthesiaRegistry := new Map<string, SynthesiaSong>
      let lastImportedMidi: {bytes: Uint8Array, md5: string}?

      applySynthesiaEntry := (bytes: Uint8Array, entry: SynthesiaSong) =>
        meta := synthesiaToPracticeMetadata bytes, entry
        writePracticeMetadata @song, meta
        fingeringDirty = true
        refreshPracticeSet() if performing()
        @notify `Imported Synthesia practice metadata${if meta.title then ` for “${meta.title}”` else ""}`

      @practiceMidiImported = (bytes) =>
        md5 := md5Hex bytes
        lastImportedMidi = {bytes, md5}
        // caller (fromBlob) pushes the undo state right after this
        if entry := synthesiaRegistry.get md5
          applySynthesiaEntry bytes, entry

      @importSynthesiaMetadata = (xml) =>
        entries := parseSynthesiaXML xml
        entries.forEach (entry) ->
          synthesiaRegistry.set normalizeMd5Id(entry.uniqueId), entry

        // the current song may be the one this file describes
        if lastImportedMidi
          if entry := synthesiaRegistry.get lastImportedMidi.md5
            applySynthesiaEntry lastImportedMidi.bytes, entry
            @unsaved true
            @pushState @getState()

      @initPractice = =>
        // The judgment windows are anchored to the rate the session started
        // at, so a rate change starts a fresh pass. Not in game mode: the
        // rate is locked to 1 there, so a slider change is inert and must
        // not wipe the running GameScore.
        @practiceRate.observe =>
          resetSession() unless @playMode() is "game"
        // A loop change is a new pass too — the old one flushes against the
        // loop it actually ran (sessionLoop), so switching drills mid-pass
        // can't attribute the dying pass to the new section
        @practiceLoop.observe resetSession

        @on "reset", =>
          noteIndex = undefined
          session = undefined
          fingeringDirty = true
          // A newly loaded song without metadata can't stay in a performance
          // mode. Refresh before touching the loop: clearing it fires
          // resetSession, which must see the new song's part
          if performing()
            refreshPracticeSet()
            unless practiceSet.size
              @playMode "edit"
          // loop beats belong to the previous song
          @practiceLoop undefined
          @practiceLoopStart undefined
        @on "changed", =>
          noteIndex = undefined
          fingeringDirty = true
          refreshPracticeSet() if performing()
        @on "initPlay", resetSession

        // A note already sounding when its channel gets muted would never see
        // its (now suppressed) noteOff — clear the part so nothing hangs
        silencePracticedPart := =>
          return unless @playing() and performing()
          practiceSet.forEach (c) =>
            @dispatchImmediateMidiData new Uint8Array [0xb0 + c, 120, 0] // all sound off
            @dispatchImmediateMidiData new Uint8Array [0xb0 + c, 123, 0] // all notes off

        // Entering practice mid-play starts fresh
        @playMode.observe =>
          silencePracticedPart()
          resetSession()

        // A natural finish shows the game results: the transport parks the
        // playhead at the end when the song runs out, so an explicit stop
        // mid-song doesn't qualify. playing flips just before the final beat
        // is written, hence the frame of slack.
        @playing.observe (playing: boolean) =>
          return if playing
          return unless @playMode() is "game" and gameScore and session
          return unless @currentBeat() >= @length() - 0.1
          @showGameResults?.({
            stats: session.stats
            score: gameScore.score
            maxCombo: gameScore.maxCombo
          })

        // Surface misses as the playhead passes unhit notes. currentBeat
        // updates from the audio clock at ~120Hz; the session's cursor makes
        // each call cheap.
        @currentBeat.observe =>
          return unless session and performing() and @playing()
          t := @currentTime()
          // The clock jumped backwards (seek or loop wrap): start over here
          if t < lastTime - HIT_WINDOW
            resetSession()
          lastTime = t
          session!.advanceTo(t).forEach ({n, startBeat}) =>
            pushFeedback n, "miss", startBeat
            recordJudgment "miss"

        addMidiEventListener (event: MIDIMessageEvent) =>
          return unless performing()
          {data, timeStamp, currentTarget} := event
          return unless data
          return unless currentTarget is @activeMidiDeviceInput()
          return unless session and @playing()
          // judge noteOns only; a noteOn with velocity 0 is a release
          return unless (data[0] & 0xf0) is 0x90 and data[2] > 0
          t := @songTimeAtPerformanceTime timeStamp
          {judgment, note} := session!.judgeNoteOn data[1], t
          // score first so the feedback carries the tier the hit just earned
          recordJudgment judgment
          pushFeedback data[1], judgment, note?.startBeat ?? @currentBeat()

  return PracticeMode

export default PracticeMode
